import {
  observable,
  computed,
  action,
  reaction,
  runInAction,
  makeObservable,
} from 'mobx'
import { v4 as uuid } from 'uuid'
import StorageManager, {
  isStorageQuotaExceededError,
} from 'services/StorageManager'
import { logger } from 'services/logger'
import { savePhotoBankApi } from 'api/photoBank.api'
import { isClient } from 'utils/runtimeUtils'
import { analytics, EVENT_NAMES } from 'services/Analytics/Analytics'
import { extractErrorMessage } from 'utils/ApiUtils'
import { getFaceMetrics } from 'services/Analytics/dpiMetrics'
import { getVaryingResolutionImageUrls } from 'components/PhotoWall/cloudinaryTransformations'
import {
  STORAGE_KEY,
  type UPLOAD_SRC,
} from 'components/PhotoBank/PhotoBank.consts'
import { isPrivatePhotoUrl } from 'components/PhotoBank/PhotoBank.utils'
import {
  safeParseLocalPhoto,
  safeParseRemotePhoto as safeParseRemotePhotoSchema,
} from 'components/PhotoBank/PhotoBank.schemas'
import { loadRemotePhotosFromStorage } from 'components/PhotoBank/photoBankSaver'
import { isExpiredPhoto } from 'components/PhotoBank/photoUtils'
import { trackOldPhotoRemoved } from 'services/Analytics/photoAnalytics/photoAnalytics'
import { CLOUD_SERVICES } from 'components/CloudTileUploader/CloudTileUploader.consts'
import { type SafeParseReturnType } from 'zod'
import type {
  RawLocalPhoto,
  LocalPhoto,
  RawRemotePhoto,
  RemotePhoto,
  Photo,
  PickedPhoto,
  UrlUpload,
} from 'components/PhotoBank/PhotoBank.types'
import type { ValueOf } from 'utils/typeUtils'
import {
  buildPhotoHashState,
  cleanupLocalPhotoResources,
  getPersistedRemotePhotos,
  orderPhotosByUid,
  preloadPhotoPreviews,
} from './PhotoBankStore.helpers'
import { type IPhotoBankUploadService } from './PhotoBankUploadService'
import { PhotoDetector } from './PhotoDetector'
import { callApi } from 'api/apiProvider'
import {
  readMobxSnapshot,
  readOutsideReaction,
} from 'utils/mobx/readOutsideReaction'
import { deepToJS } from 'utils/mobx/deepToJS'

export interface UploadPhotosProps {
  cloudServiceName: string | null
  files: PickedPhoto[]
  onUploadFailed: (uid: string) => void
  uploadSrc: ValueOf<typeof UPLOAD_SRC>
  delayUpload?: boolean
  includeHappyData?: boolean
  includePetData?: boolean
  onUploadProgress?: (progress: number) => void
  withFaceDetection?: boolean
  filterSmallFaces?: boolean
}

export type StartUploadCallback = (
  onUploadFailed: (uid: string) => void
) => void

export type UploadResult = Promise<{
  photos: Photo[]
  startUpload?: StartUploadCallback
  pendingPhotos?: LocalPhoto[]
}>

type PhotoCapReachedProperties =
  | { Source: 'Persist'; 'Photo Count': number }
  | { Source: 'Load'; 'Photo Count': number; 'Dropped Count': number }

const MAX_ITEMS_IN_PHOTO_BANK = 1000

const EXPIRED_PHOTO_SOURCES = {
  STORAGE: 'Photo Bank',
  RESTORE: 'Photo Bank Restore',
} as const

export class PhotoBankStore {
  @observable accessor remotePhotosMap: Map<string, RemotePhoto> = new Map()

  @observable accessor localPhotosMap: Map<string, LocalPhoto> = new Map()

  @observable.shallow accessor uploadPromisesMap: Map<
    string,
    Promise<RemotePhoto | undefined>
  > = new Map()

  // Gates backend sync: true only after the account's photos are merged in, so we never push before we pull.
  @observable accessor isAccountSyncReady = false

  private pendingPhotosToRemove = new Set<string>()

  // The persist reaction re-runs 150ms after every change, so each report
  // fires once per page.
  private hasReportedPhotoCap = false

  private hasReportedFullStorage = false

  private uploadService: IPhotoBankUploadService

  readonly photoDetector: PhotoDetector

  constructor(uploadService: IPhotoBankUploadService) {
    this.uploadService = uploadService
    this.photoDetector = new PhotoDetector(this, uploadService)

    if (isClient()) {
      this.initializeFromStorage()
      this.setupAutorun()
    }
    makeObservable(this)
  }

  @computed
  get remotePhotos(): RemotePhoto[] {
    return Array.from(this.remotePhotosMap.values())
  }

  @computed
  get localPhotos(): LocalPhoto[] {
    return Array.from(this.localPhotosMap.values())
  }

  @computed
  get photos(): Photo[] {
    const remoteUids = new Set(this.remotePhotosMap.keys())
    const localPhotosNotInRemote = this.localPhotos.filter(
      (p) => !remoteUids.has(p.uid)
    )
    return [...this.remotePhotos, ...localPhotosNotInRemote]
  }

  @computed
  get hasPendingUploads(): boolean {
    return this.localPhotosMap.size > 0
  }

  getPhoto = (uid: string): Photo | undefined => {
    // Read both maps so MobX tracks either source when a photo flips from local to remote.
    const remotePhoto = this.remotePhotosMap.get(uid)
    const localPhoto = this.localPhotosMap.get(uid)

    return remotePhoto ?? localPhoto
  }

  @action
  updatePhoto = (uid: string, updater: (photo: Photo) => void): void => {
    const target = this.getPhoto(uid)
    if (!target) return
    updater(target)
  }

  @action
  updatePhotos = (uids: string[], updater: (photo: Photo) => void): void => {
    uids.forEach((uid) => this.updatePhoto(uid, updater))
  }

  /** @internal — do not use outside PhotoBankStore */
  @action
  setRemotePhoto = (photo: RawRemotePhoto): RemotePhoto | undefined => {
    const parsedPhoto = this.safeParseRemotePhoto(photo)
    if (!parsedPhoto.success) {
      return undefined
    }

    return this.setTypedRemotePhoto(parsedPhoto.data)
  }

  @action
  deleteRemotePhoto = (uid: string): void => {
    this.remotePhotosMap.delete(uid)
  }

  /** @internal — do not use outside PhotoBankStore */
  @action
  setLocalPhoto = (photo: RawLocalPhoto): LocalPhoto => {
    const parsedPhoto = safeParseLocalPhoto(photo)
    if (!parsedPhoto.success) {
      logger.error('Invalid local photo payload', parsedPhoto.error)
      throw new Error('Invalid local photo payload')
    }

    const localPhoto = parsedPhoto.data
    this.localPhotosMap.set(photo.uid, localPhoto)

    // We must get the photo from the map after setting it so we get the observable version of it
    return this.localPhotosMap.get(photo.uid)!
  }

  @action
  deleteLocalPhoto = (uid: string): void => {
    const photo = this.localPhotosMap.get(uid)
    if (!photo) return

    this.localPhotosMap.delete(uid)
    cleanupLocalPhotoResources(photo)
  }

  // Merge the account's photos in, then enable sync — so the first backend write reflects
  // the merged set, never an empty not-yet-loaded state. Called once, from AccountProvider.
  @action
  onAccountSyncReady = (accountPhotos: RawRemotePhoto[]): void => {
    this.appendPhotos({ photos: accountPhotos, sendRestoreAnalytics: true })
    this.isAccountSyncReady = true
  }

  @action
  onSignedOut = (): void => {
    this.isAccountSyncReady = false
  }

  // For logout paths that reload immediately: wipes synchronously (can't wait for the
  // debounced persist because we are going to reload) and clears storage so photos
  // don't leak into the next account.
  @action
  clearMemoryAndStorage = (): void => {
    this.localPhotosMap.forEach((photo) => cleanupLocalPhotoResources(photo))
    this.remotePhotosMap.clear()
    this.localPhotosMap.clear()
    this.uploadPromisesMap.clear()
    this.pendingPhotosToRemove.clear()
    this.isAccountSyncReady = false
    StorageManager.remove(STORAGE_KEY)
  }

  @action
  removePhoto = (uid: string): void => {
    if (!this.remotePhotosMap.has(uid)) {
      this.pendingPhotosToRemove.add(uid)
    }

    this.deleteLocalPhoto(uid)
    this.deleteRemotePhoto(uid)
  }

  @action
  getRemotePhotoAsync = async (
    uid: string
  ): Promise<RemotePhoto | undefined> => {
    const remotePhoto = this.remotePhotosMap.get(uid)
    if (remotePhoto) {
      return remotePhoto
    }
    return this.uploadPromisesMap.get(uid)
  }

  @action
  getRemotePhotoUrlAsync = async (uid: string): Promise<string | undefined> => {
    const remotePhoto = this.remotePhotosMap.get(uid)
    if (remotePhoto) {
      return remotePhoto.originalUrl
    }
    const uploadPromiseResult = await this.uploadPromisesMap.get(uid)
    return readOutsideReaction(() => uploadPromiseResult?.originalUrl)
  }

  didPhotoUpload = (uid: string): boolean => {
    const photo = this.remotePhotosMap.get(uid)
    return !!photo?.uploadedAt
  }

  uploadPhotos = async ({
    cloudServiceName,
    files,
    onUploadFailed,
    uploadSrc,
    delayUpload = false,
    includeHappyData = false,
    includePetData = false,
    onUploadProgress,
    withFaceDetection = true,
    filterSmallFaces = false,
  }: UploadPhotosProps): UploadResult => {
    const shouldUploadFromUrl =
      cloudServiceName &&
      cloudServiceName !== CLOUD_SERVICES.GOOGLE_PHOTOS_PICKER

    const result = shouldUploadFromUrl
      ? await this.uploadPhotosFromUrls(
          files as UrlUpload[],
          uploadSrc,
          includeHappyData,
          includePetData,
          onUploadProgress,
          withFaceDetection,
          filterSmallFaces
        )
      : await this.uploadPhotosFromDevice({
          files: files as File[],
          onUploadFailed,
          uploadSrc,
          delayUpload,
          includeHappyData,
          includePetData,
          withFaceDetection,
          filterSmallFaces,
        })

    return readMobxSnapshot(() => result)
  }

  @action
  private addUploadPromises = (
    newPhotos: LocalPhoto[],
    onUploadFailed: (uid: string) => void,
    withFaceDetection: boolean
  ): void => {
    if (newPhotos.length === 0) return

    const uploadPromises = newPhotos.map((photo) => {
      const promise = this.uploadSinglePhoto(
        photo,
        onUploadFailed,
        withFaceDetection
      ).catch((e) => {
        logger.error('failed uploading photo in bank', e)
        return undefined
      })

      return { uid: photo.uid, promise }
    })

    // Update the uploadPromisesMap in a single runInAction to avoid multiple re-renders
    runInAction(() => {
      uploadPromises.forEach(({ uid, promise }) => {
        this.uploadPromisesMap.set(uid, promise)
      })
    })
  }

  @action
  appendPhotos = ({
    photos,
    uploadSrc,
    sendRestoreAnalytics = false,
  }: {
    photos: RawRemotePhoto[]
    uploadSrc?: ValueOf<typeof UPLOAD_SRC>
    sendRestoreAnalytics?: boolean
  }): RemotePhoto[] => {
    const newPhotos: RemotePhoto[] = []

    for (const incomingPhoto of photos) {
      if (!uploadSrc && !incomingPhoto.uid) continue

      const photoToValidate = this.normalizePhotoForAppend(
        incomingPhoto,
        uploadSrc
      )

      const parsedPhoto = this.safeParseRemotePhoto(photoToValidate)
      if (!parsedPhoto.success) {
        logger.error('Invalid external remote photo payload', parsedPhoto.error)
        continue
      }

      const remotePhoto = parsedPhoto.data
      if (this.remotePhotosMap.has(remotePhoto.uid)) continue

      // Restore paths (account sync, saved carts, other browsers) bypass the
      // storage-load TTL filter; an expired photo points at a purged S3 object.
      if (isExpiredPhoto(remotePhoto)) {
        this.reportExpiredPhotoDropped(
          remotePhoto,
          EXPIRED_PHOTO_SOURCES.RESTORE
        )
        continue
      }

      newPhotos.push(this.setTypedRemotePhoto(remotePhoto))
    }

    if (sendRestoreAnalytics && newPhotos.length > 0) {
      analytics.track(EVENT_NAMES.RESTORED_PHOTO_BANK, {
        'Photos Count': newPhotos.length,
      })
    }

    return newPhotos
  }

  private initializeFromStorage(): void {
    const photos = this.loadValidPhotosFromStorage()
    photos.forEach((photo) => this.setTypedRemotePhoto(photo))
  }

  private setupAutorun() {
    reaction(
      () => getPersistedRemotePhotos(this.remotePhotos),
      (photos: RawRemotePhoto[]) => {
        photos = deepToJS(photos)
        if (photos.length > MAX_ITEMS_IN_PHOTO_BANK) {
          this.reportPhotoCapReached({
            Source: 'Persist',
            'Photo Count': photos.length,
          })
        }
        try {
          StorageManager.setOrThrow(STORAGE_KEY, photos)
        } catch (err) {
          this.reportPersistFailure(err, photos.length)
        }
      },
      {
        name: 'PhotoBankStore:persistToLocalStorage',
        delay: 150,
        fireImmediately: true,
      }
    )

    reaction(
      () => ({
        isAccountSyncReady: this.isAccountSyncReady,
        remotePhotos: getPersistedRemotePhotos(this.remotePhotos),
      }),
      ({ isAccountSyncReady, remotePhotos }) => {
        if (!isAccountSyncReady) return

        remotePhotos = deepToJS(remotePhotos)
        callApi(savePhotoBankApi(remotePhotos)).catch((err) => {
          logger.error('Failed to sync photo bank to backend', err, {
            'Photos Count': remotePhotos.length,
          })
        })
      },
      {
        name: 'PhotoBankStore:syncToBackend',
        delay: 1000,
        fireImmediately: true,
      }
    )
  }

  @action
  private uploadPhotosFromUrls = async (
    files: UrlUpload[],
    uploadSrc: string,
    includeHappyData: boolean,
    includePetData: boolean,
    onUploadProgress: UploadPhotosProps['onUploadProgress'],
    withFaceDetection: boolean,
    filterSmallFaces: boolean
  ): UploadResult => {
    const newPhotos: RemotePhoto[] = []
    const existingPhotos: RemotePhoto[] = []
    const orderedPhotoUids: string[] = []
    let currentIndex = 0

    for (const { url, width: knownWidth, height: knownHeight } of files) {
      currentIndex++
      onUploadProgress?.((currentIndex / files.length) * 90)

      const existingPhoto = readOutsideReaction(() =>
        this.findRemotePhotoByUrl(url)
      )
      if (existingPhoto) {
        existingPhotos.push(existingPhoto)
        orderedPhotoUids.push(readOutsideReaction(() => existingPhoto.uid))
        continue
      }

      if (knownWidth === undefined || knownHeight === undefined) {
        logger.info('uploadPhotosFromUrls: loadImageMeasurements', { url })
      }
      const { width, height } =
        knownWidth !== undefined && knownHeight !== undefined
          ? { width: knownWidth, height: knownHeight }
          : await this.uploadService.getImageDimensions(url)
      const { minimalResUrl, lowResUrl, highResUrl } =
        getVaryingResolutionImageUrls({
          url,
          width,
          height,
        })

      const hashCode = await this.uploadService.hashUrl(url)

      const newPhoto: RawRemotePhoto = {
        width,
        height,
        uid: uuid(),
        originalUrl: url,
        minimalResUrl,
        lowResUrl,
        highResUrl,
        uploadSrc,
        uploadedAt: Date.now(),
        wasUsed: false,
        hashCode,
        faceDetectionSuccess: null,
        smallFacesFiltered: 0,
      }

      const remotePhoto = this.setRemotePhoto(newPhoto)
      if (!remotePhoto) {
        throw new Error('Invalid remote photo payload')
      }

      newPhotos.push(remotePhoto)
      orderedPhotoUids.push(readOutsideReaction(() => remotePhoto.uid))
    }

    if (withFaceDetection && newPhotos.length > 0) {
      await this.photoDetector.detectFromUrls(newPhotos, {
        includeHappyData,
        includePetData,
        filterSmallFaces,
      })
    }

    onUploadProgress?.(100)

    return readOutsideReaction(() => ({
      photos: orderPhotosByUid(orderedPhotoUids, [
        ...newPhotos,
        ...existingPhotos,
      ]),
    }))
  }

  @action
  private uploadPhotosFromDevice = async ({
    files,
    onUploadFailed,
    uploadSrc,
    delayUpload = false,
    includeHappyData = false,
    includePetData,
    withFaceDetection,
    filterSmallFaces = false,
  }: {
    files: File[]
    onUploadFailed: (uid: string) => void
    uploadSrc: string
    delayUpload: boolean
    includeHappyData: boolean
    includePetData: boolean
    withFaceDetection: boolean
    filterSmallFaces?: boolean
  }): UploadResult => {
    const newPhotos: LocalPhoto[] = []
    const existingLocalPhotos: LocalPhoto[] = []
    const existingRemotePhotos: RemotePhoto[] = []
    const faceDetectionBlobs: Record<string, Blob> = {}
    const petDetectionBlobs = new Map<string, Blob>()
    const orderedPhotoUids: string[] = []
    const { knownHashes, localHashToPhoto, remoteHashToPhoto } =
      buildPhotoHashState(this.localPhotos, this.remotePhotos)

    const processPromises = files.map(async (file) => {
      try {
        const result = await this.uploadService.processFile(
          file,
          uploadSrc,
          knownHashes,
          withFaceDetection && includePetData
        )

        if (result.type === 'known') {
          const existingLocal = localHashToPhoto.get(result.hashCode)
          if (existingLocal) {
            return { type: 'existing-local' as const, photo: existingLocal }
          }
          const existingRemote = remoteHashToPhoto.get(result.hashCode)!
          return { type: 'existing-remote' as const, photo: existingRemote }
        }

        return {
          type: 'new' as const,
          localPhoto: result.localPhoto,
          faceDetectionBlob: result.faceDetectionBlob,
          petDetectionBlob: result.petDetectionBlob,
        }
      } catch (e) {
        logger.error('Processing file thumb failed', e)
        analytics.track(EVENT_NAMES.LOCAL_PHOTO_UPLOAD_FAILED, {
          Error: extractErrorMessage(e),
        })
        const failedUid = uuid()
        onUploadFailed(failedUid)
        return { type: 'failed' as const, uid: failedUid }
      }
    })

    const results = await Promise.all(processPromises)

    runInAction(() => {
      results.forEach((result) => {
        if (result.type === 'new') {
          const localPhoto = this.setLocalPhoto(result.localPhoto)
          newPhotos.push(localPhoto)
          faceDetectionBlobs[localPhoto.uid] = result.faceDetectionBlob
          if (result.petDetectionBlob) {
            petDetectionBlobs.set(localPhoto.uid, result.petDetectionBlob)
          }
          orderedPhotoUids.push(localPhoto.uid)
        } else if (result.type === 'existing-local') {
          existingLocalPhotos.push(result.photo)
          orderedPhotoUids.push(result.photo.uid)
        } else if (result.type === 'existing-remote') {
          existingRemotePhotos.push(result.photo)
          orderedPhotoUids.push(result.photo.uid)
        } else if (result.type === 'failed') {
          orderedPhotoUids.push(result.uid)
        }
      })
    })

    if (withFaceDetection && newPhotos.length > 0) {
      const fileObjects = newPhotos.map((photo) => {
        const blob = readOutsideReaction(() => faceDetectionBlobs[photo.uid])
        return new File(blob ? [blob] : [], `${uuid()}.jpg`, {
          type: blob?.type,
        })
      })

      await this.photoDetector.detectFromFiles(
        newPhotos,
        fileObjects,
        petDetectionBlobs,
        { includeHappyData, includePetData, filterSmallFaces }
      )
    }

    const orderedPhotos = readOutsideReaction(() =>
      orderPhotosByUid(orderedPhotoUids, [
        ...existingRemotePhotos,
        ...existingLocalPhotos,
        ...newPhotos,
      ])
    )

    if (delayUpload) {
      if (newPhotos.length === 0) {
        return { photos: orderedPhotos }
      }
      const logNotStartedTimeoutId = setTimeout(() => {
        logger.warning(
          'uploadPhotos was called with delayUpload but no startUpload was called within 10 seconds'
        )
      }, 10000)
      return {
        photos: orderedPhotos,
        startUpload: (onFailed: (uid: string) => void) => {
          clearTimeout(logNotStartedTimeoutId)
          this.addUploadPromises(newPhotos, onFailed, withFaceDetection)
        },
        pendingPhotos: newPhotos,
      }
    } else {
      this.addUploadPromises(newPhotos, onUploadFailed, withFaceDetection)
      return {
        photos: orderedPhotos,
      }
    }
  }

  uploadSinglePhoto = async (
    photo: LocalPhoto,
    onUploadFailed: (uid: string) => void,
    withFaceDetection: boolean
  ): Promise<RemotePhoto> => {
    const {
      originalFile: file,
      uid,
      width,
      height,
      uploadSrc,
      faces,
      faceDetectionSuccess,
      smallFacesFiltered,
      hashCode,
    } = photo

    try {
      const originalUrl = await this.uploadService.uploadFileToS3(
        file,
        { width, height },
        {
          'Upload Src': uploadSrc,
          'Face Detection Requested': withFaceDetection,
          ...getFaceMetrics({ faces, width, height, smallFacesFiltered }),
        }
      )
      // The photo stays local until the browser has loaded its previews, so it never shows an empty image in between
      if (isPrivatePhotoUrl(originalUrl)) {
        await preloadPhotoPreviews(originalUrl)
      }

      const remotePhoto: RawRemotePhoto = {
        width,
        height,
        uid,
        originalUrl,
        uploadSrc,
        hashCode,
        uploadedAt: Date.now(),
        wasUsed: false,
        faces,
        faceDetectionSuccess,
        smallFacesFiltered,
      }

      const parsedPhoto = readOutsideReaction(() =>
        this.safeParseRemotePhoto(remotePhoto)
      )
      if (!parsedPhoto.success) {
        logger.error('Invalid uploaded remote photo payload', parsedPhoto.error)
        throw new Error('Invalid uploaded remote photo payload')
      }
      let remotePhotoWithType = parsedPhoto.data

      runInAction(() => {
        if (!this.pendingPhotosToRemove.has(uid)) {
          // wasUsed and petDetection can be changed during upload so take them from the updated photo
          remotePhotoWithType.wasUsed = photo.wasUsed
          remotePhotoWithType.petDetection = photo.petDetection
          remotePhotoWithType = this.setTypedRemotePhoto(remotePhotoWithType)
        } else {
          this.pendingPhotosToRemove.delete(uid)
        }
        this.uploadPromisesMap.delete(uid)
      })

      return remotePhotoWithType
    } catch (e) {
      onUploadFailed(uid)
      throw e
    }
  }

  private findRemotePhotoByUrl = (url: string): RemotePhoto | undefined => {
    for (const photo of this.remotePhotosMap.values()) {
      if (photo.originalUrl === url) return photo
    }
    return undefined
  }

  @action
  private setTypedRemotePhoto(photo: RemotePhoto): RemotePhoto {
    this.deleteLocalPhoto(photo.uid)
    this.remotePhotosMap.set(photo.uid, photo)

    // We must get the photo from the map after setting it so we get the observable version of it
    return this.remotePhotosMap.get(photo.uid)!
  }

  private safeParseRemotePhoto(
    photo: RawRemotePhoto
  ): SafeParseReturnType<unknown, RemotePhoto> {
    return safeParseRemotePhotoSchema(photo)
  }

  private normalizePhotoForAppend(
    photo: RawRemotePhoto,
    uploadSrc: ValueOf<typeof UPLOAD_SRC> | undefined
  ): RawRemotePhoto {
    const normalizedPhoto: RawRemotePhoto = {
      ...photo,
      uid: photo.uid || uuid(),
      uploadSrc: uploadSrc ?? photo.uploadSrc,
      wasUsed: true,
    }

    if (
      normalizedPhoto.lowResUrl &&
      normalizedPhoto.minimalResUrl &&
      normalizedPhoto.highResUrl
    ) {
      // A restored photo may carry a stored 700px lowResUrl; serve the minimal
      // one instead so that Cloudinary asset is never requested.
      return { ...normalizedPhoto, lowResUrl: normalizedPhoto.minimalResUrl }
    }

    const { minimalResUrl, lowResUrl, highResUrl } =
      getVaryingResolutionImageUrls({
        url: normalizedPhoto.originalUrl,
        width: normalizedPhoto.width,
        height: normalizedPhoto.height,
      })

    return {
      ...normalizedPhoto,
      minimalResUrl,
      lowResUrl,
      highResUrl,
    }
  }

  private reportExpiredPhotoDropped(
    photo: RemotePhoto,
    source: ValueOf<typeof EXPIRED_PHOTO_SOURCES>
  ): void {
    trackOldPhotoRemoved({
      photoUploadTime: photo.uploadedAt,
      source,
    })
    logger.info('Removed Old Photo from Photo Bank', {
      'Photo Upload Time': photo.uploadedAt,
      Source: source,
    })
  }

  private reportPhotoCapReached(properties: PhotoCapReachedProperties): void {
    if (this.hasReportedPhotoCap) return

    this.hasReportedPhotoCap = true
    const capReport = { ...properties, Cap: MAX_ITEMS_IN_PHOTO_BANK }
    logger.error('Photo bank exceeded its photo cap', null, capReport)
    analytics.track(EVENT_NAMES.PHOTO_BANK_CAP_REACHED, capReport)
  }

  private reportPersistFailure(err: unknown, photoCount: number): void {
    if (!isStorageQuotaExceededError(err)) {
      logger.error('Error while setting photos in storage', err)
      return
    }
    if (this.hasReportedFullStorage) return

    this.hasReportedFullStorage = true
    logger.error('Photo bank storage is full', err, {
      'Photo Count': photoCount,
    })
    analytics.track(EVENT_NAMES.PHOTO_BANK_STORAGE_FULL, {
      'Photo Count': photoCount,
    })
  }

  private loadValidPhotosFromStorage(): RemotePhoto[] {
    const sortedPhotos = loadRemotePhotosFromStorage()
      .reduce<RemotePhoto[]>((photos, photo) => {
        const parsedPhoto = this.safeParseRemotePhoto(photo)
        if (!parsedPhoto.success) {
          logger.error(
            'Invalid remote photo in storage, skipping',
            parsedPhoto.error
          )
          return photos
        }

        photos.push(parsedPhoto.data)
        return photos
      }, [])
      .filter((photo) => {
        if (!isExpiredPhoto(photo)) {
          return true
        }

        this.reportExpiredPhotoDropped(photo, EXPIRED_PHOTO_SOURCES.STORAGE)
        return false
      })
      .sort((a, b) => b.uploadedAt - a.uploadedAt)

    if (sortedPhotos.length > MAX_ITEMS_IN_PHOTO_BANK) {
      this.reportPhotoCapReached({
        Source: 'Load',
        'Photo Count': sortedPhotos.length,
        'Dropped Count': sortedPhotos.length - MAX_ITEMS_IN_PHOTO_BANK,
      })
    }

    return sortedPhotos.slice(0, MAX_ITEMS_IN_PHOTO_BANK)
  }
}
